import { FC, useContext, useEffect, useMemo, useRef, useState } from "react";
import { InfoContext, MasterContext, UIContext } from "../../../shared/contexts";
import { Form } from "antd";
import { PageHeader } from "../../../shared/components/MainLayout/components";
import { CardSkeletons, ColSize, ErrorResults, InputFactory, RowSize } from "../../../shared/components";
import { Grid } from "@hdi-seguros/web-components.grid";
import { VendorCard } from "./components/VendorCard";
import { ItemNotFound } from "../../../shared/components/ItemNotFound";
import { InsurancePolicyNotFound } from "../../../assets";
import { inputsFilter } from "./utils/inputs-vendor";
import { TourComponent } from "../../../shared/components/Tour";
import VendorsService from "../../../shared/services/vendors";
import { GetVendorsFilter, GetVendorsListServiceResponse, Vendor } from "../../../shared/interfaces/get-vendors-service";
import { VendorType } from "../../../shared/interfaces/enums/vendor-values-enum";

interface VendorListProps {
  title: string;
}

export const VendorListPage: FC<VendorListProps> = ({ title }) => {
  const { isLoading, showError, setIsLoading, setShowError } = useContext(UIContext);
  const { setTitle } = useContext(InfoContext);
  const { branchList, getBranchsList } = useContext(MasterContext);
  const gridRef = useRef();
  const filters = useMemo(() => inputsFilter, []);
  const [isLoadingFilters, setIsLoadingFilters] = useState(false);

  const [vendorList, setVendorList] = useState<GetVendorsListServiceResponse>({proveedores: []});
  const [vendorUnique, setVendorUnique] = useState<Array<Vendor>>(vendorList.proveedores || []);
  const [showTour, setShowTour] = useState<boolean>(false);

  const [form] = Form.useForm();


  const getVendorList = async () => {
    setIsLoading(true);
    setShowError(false);

    try {
      const filters: GetVendorsFilter = form.getFieldsValue();
      const response = await VendorsService.getVendors(filters);

      if (response) {
        setVendorList({ ...response });
      }
    } catch (error) {
      setShowError(true);
    } finally {
      setTimeout(() => {
        setIsLoading(false);
      }, 0);
    }
  };

  const initialStateFilters = useMemo(
    () => ({
      name: "",
      vendorType: VendorType.TODOS
    }),
    []
  );

  const actions = useMemo(
    () => [
        
    ],
    []
  );

  useEffect(() => {
    setIsLoading(true);
    setTitle(title);
  }, []);

  useEffect(() => {
    if (vendorList && gridRef.current) {
      (gridRef.current as any).setPage(1);
    }
  }, [vendorList, setVendorList]);

  useEffect(() => {
    if (!branchList?.length) getBranchsList();
  }, [branchList]);

  const stepsTour = [
    {
      target: ".step-1",
      content: (
        <p>
          ✅ Filtrá tus proveedores fácilmente. Usá estos filtros para buscar gomerías, cristalerías y talleres; por nombre y
          tipo de proveedor. Combiná varios criterios para obtener resultados más precisos.
        </p>
      ),
      disableBeacon: true,
    },
    {
      target: ".step-2",
      content: (
        <p>
           👤 Buscá por nombre. Ideal cuando no tenés el código interno a mano.
        </p>
      ),
      disableBeacon: true,
    },
    {
      target: ".step-3",
      content: (
        <p>
          🏪 Indicá el tipo de proveedor. Podés filtrar entre gomerías, cristales o talleres mecánicos para acotar la búsqueda.
        </p>
      ),
      disableBeacon: true,
    },
    {
      target: "body",
      content: (
        <p>
          💡 Combiná filtros para mejores resultados. Por ejemplo: gomerías y cuyo nombre sea Ejemplo.
          Cuanto más específico seas, más rápido vas a llegar al proveedor que necesitás.
        </p>
      ),
      disableBeacon: true,
    },
  ];

  const toggleTour = () => {
    setShowTour(!showTour);
  };

  return (
    <>
      <PageHeader activeTour={toggleTour} actions={actions} />
      <TourComponent showTour={showTour} steps={stepsTour} onTourClose={toggleTour} />

      <RowSize rowProps={{ gutter: [0, 0] }} className="vendor-list__filter-row">
        <InputFactory
          form={form}
          items={filters}
          onFilter={getVendorList}
          onClear={getVendorList}
          isLoading={isLoadingFilters}
          initialState={initialStateFilters}
          className="step-1"
        />
      </RowSize>

      {showError ? (
        <ErrorResults title="No se pudo cargar el listado de proveedores" />
      ) : (
        <RowSize
          rowProps={{
            gutter: [15, 15],
            className: "vendor-list__card-margin-fix",
          }}
        >
          <ColSize size={100}>
            <Grid list={vendorList?.proveedores || []} setState={setVendorUnique} name="vendors" ref={gridRef}>
              <ColSize size={100}>
                {vendorUnique?.length ? (
                  vendorUnique
                    .filter((vendor) => Object.keys(vendor).length > 0)
                    .map((vendor, index) => (
                      <VendorCard
                        key={`VendorCard-${vendor.codigo}-${index}`}
                        vendor={vendor}
                        loading={isLoading}
                      />
                    ))
                ) : isLoading ? (
                  <RowSize rowProps={{ gutter: [15, 15] }}>
                    <CardSkeletons />
                  </RowSize>
                ) : (
                  <ItemNotFound
                    textDescription="No se encontraron proveedores"
                    imageUrl={InsurancePolicyNotFound}
                  />
                )}
              </ColSize>
            </Grid>
          </ColSize>
        </RowSize>
      )}
    </>
  );
};

export {};
